import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";
import type { LostCause } from "../../services/ShiftAnalysisService";
import Panel from "./Panel";

interface Props {
  causes: LostCause[];
}

const isInternal = (rating: string) => rating.toLowerCase().startsWith("int");

/**
 * "Lost production by cause" — ranked bars of products lost per stop cause,
 * colored by rating (internal = our fault, external = upstream/downstream/
 * setup). Backed by shift_class_agg; renders nothing when there's no data.
 */
const LostProduction: React.FC<Props> = ({ causes }) => {
  const { t } = useTranslation();

  const { top, max, totalLost } = useMemo(() => {
    const withLoss = causes.filter((c) => c.lost_production > 0);
    const top = withLoss.slice(0, 8);
    const max = top.reduce((m, c) => Math.max(m, c.lost_production), 1);
    const totalLost = withLoss.reduce((s, c) => s + c.lost_production, 0);
    return { top, max, totalLost };
  }, [causes]);

  if (top.length === 0) return null;

  return (
    <Panel
      label={t("lostProduction", "Lost production")}
      meta={t("byCause", "by cause")}
      right={
        <span
          className="text-xs tabular-nums font-semibold"
          style={{ color: "var(--text-primary)" }}
        >
          {totalLost.toLocaleString()}{" "}
          <span
            className="font-normal"
            style={{ color: "var(--text-secondary)" }}
          >
            {t("productsLost", "lost")}
          </span>
        </span>
      }
    >
      <div className="flex flex-col gap-2.5">
        {top.map((c) => {
          const internal = isInternal(c.rating);
          const color = internal ? "#ef4444" : "#f59e0b";
          const pct = (c.lost_production / max) * 100;
          return (
            <div key={`${c.cause}-${c.rating}`} className="flex flex-col gap-1">
              <div className="flex items-baseline justify-between gap-3">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="text-[9px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded"
                    style={{ backgroundColor: `${color}1f`, color }}
                  >
                    {internal ? t("int", "Int") : t("ext", "Ext")}
                  </span>
                  <span
                    className="text-sm truncate"
                    style={{ color: "var(--text-primary)" }}
                    title={c.cause}
                  >
                    {c.cause}
                  </span>
                </div>
                <div className="flex items-baseline gap-2 shrink-0">
                  <span
                    className="text-sm font-bold tabular-nums"
                    style={{ color: "var(--text-primary)" }}
                  >
                    {c.lost_production.toLocaleString()}
                  </span>
                  {c.stops > 0 && (
                    <span
                      className="text-[11px] tabular-nums"
                      style={{ color: "var(--text-secondary)" }}
                    >
                      · {c.stops} {t("stops", "stops")}
                    </span>
                  )}
                </div>
              </div>
              <div
                className="h-2 w-full rounded-full overflow-hidden"
                style={{ backgroundColor: "var(--bg-secondary)" }}
              >
                <div
                  className="h-full rounded-full transition-[width] duration-700 ease-out"
                  style={{
                    width: `${Math.max(pct, 2)}%`,
                    background: `linear-gradient(90deg, ${color}, ${color}bb)`,
                  }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </Panel>
  );
};

export default LostProduction;
